import React from 'react';
import { ThemeColor, getPreviewColor } from '../contexts/ThemeContext';

interface ThemeColorPickerProps {
  label: string;
  color: ThemeColor;
  onChange: (color: ThemeColor) => void;
  isDark: boolean;
}

export const ThemeColorPicker: React.FC<ThemeColorPickerProps> = ({
  label,
  color,
  onChange,
  isDark,
}) => {
  // Safe defaults if color is missing or malformed
  const safeHue = color?.hue ?? 220;
  const safeSaturation = color?.saturation ?? 0;

  const handleHueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ hue: parseInt(e.target.value, 10), saturation: safeSaturation });
  };

  const handleSaturationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ hue: safeHue, saturation: parseInt(e.target.value, 10) });
  };

  // Generate preview colors for the three background levels
  const getPreviewColors = () => {
    const sat = safeSaturation * 0.5;
    if (isDark) {
      return {
        primary: `hsl(${safeHue}, ${sat}%, 7%)`,
        secondary: `hsl(${safeHue}, ${sat}%, 12%)`,
        tertiary: `hsl(${safeHue}, ${sat}%, 20%)`,
      };
    } else {
      return {
        primary: `hsl(${safeHue}, ${sat}%, 99%)`,
        secondary: `hsl(${safeHue}, ${sat * 1.2}%, 96%)`,
        tertiary: `hsl(${safeHue}, ${sat * 1.5}%, 92%)`,
      };
    }
  };

  const previewColors = getPreviewColors();

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-heading block">
        {label}
      </label>

      {/* Preview swatches showing the 3 color variants */}
      <div className="flex gap-2 mb-3">
        <div className="flex-1 text-center">
          <div
            className="h-10 rounded-md border border-gray-300 dark:border-gray-600 mb-1"
            style={{ backgroundColor: previewColors.primary }}
          />
          <span className="text-xs text-muted">Primary</span>
        </div>
        <div className="flex-1 text-center">
          <div
            className="h-10 rounded-md border border-gray-300 dark:border-gray-600 mb-1"
            style={{ backgroundColor: previewColors.secondary }}
          />
          <span className="text-xs text-muted">Secondary</span>
        </div>
        <div className="flex-1 text-center">
          <div
            className="h-10 rounded-md border border-gray-300 dark:border-gray-600 mb-1"
            style={{ backgroundColor: previewColors.tertiary }}
          />
          <span className="text-xs text-muted">Tertiary</span>
        </div>
      </div>

      {/* Hue slider with rainbow gradient */}
      <div>
        <div className="flex justify-between mb-1">
          <span className="text-xs text-muted">Hue</span>
          <span className="text-xs text-muted">{safeHue}°</span>
        </div>
        <input
          type="range"
          min="0"
          max="360"
          value={safeHue}
          onChange={handleHueChange}
          className="w-full h-3 rounded-lg appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right,
              hsl(0, 80%, 50%),
              hsl(60, 80%, 50%),
              hsl(120, 80%, 50%),
              hsl(180, 80%, 50%),
              hsl(240, 80%, 50%),
              hsl(300, 80%, 50%),
              hsl(360, 80%, 50%)
            )`,
          }}
        />
      </div>

      {/* Saturation slider */}
      <div>
        <div className="flex justify-between mb-1">
          <span className="text-xs text-muted">Intensity</span>
          <span className="text-xs text-muted">{safeSaturation}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          value={safeSaturation}
          onChange={handleSaturationChange}
          className="w-full h-3 rounded-lg appearance-none cursor-pointer"
          style={{
            background: isDark
              ? `linear-gradient(to right,
                  hsl(${safeHue}, 0%, 12%),
                  hsl(${safeHue}, 50%, 12%)
                )`
              : `linear-gradient(to right,
                  hsl(${safeHue}, 0%, 96%),
                  hsl(${safeHue}, 50%, 96%)
                )`,
          }}
        />
      </div>

      {/* Reset button */}
      <button
        onClick={() => onChange({ hue: 220, saturation: 0 })}
        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
      >
        Reset to neutral
      </button>
    </div>
  );
};

export default ThemeColorPicker;
